import { capitalize, labelToProp } from '../strings.ts';
import type { buildMeasure } from './factory.ts';
import type { Quantity } from './quantity.ts';
import { ProductOf, RatioOf } from './quantity.ts';
import type { Scalar } from './scalar.ts';
import type { Vector } from './vector.ts';

export enum Punct {
  Dot = '⋅',
  One = '¹',
  Two = '²',
  Neg = '⁻',
  NegOne = '⁻¹',
  NegTwo = '⁻²',
}

const unitLabelAliases: Record<string, string> = {
  'meters per seconds per seconds': 'meters per seconds squared',
  'kilograms dot meters per seconds squared': 'newtons',
  'cycles per second': 'hertz',
};

const unitAliases: Record<string, string> = {
  [`m${Punct.Dot}s${Punct.NegOne}${Punct.Dot}s${Punct.NegOne}`]: `m${Punct.Dot}s${Punct.NegTwo}`,
  [`kg${Punct.Dot}m${Punct.Dot}s${Punct.NegTwo}`]: 'N',
  [`cyc${Punct.Dot}s${Punct.NegOne}`]: `Hz`,
};

const quantityTypeAliases: Record<string, string> = {
  'length/time': 'velocity',
  'velocity/time': 'acceleration',
  'mass*acceleration': 'force',
  'cycles/time': 'frequency',
};

export const Factory: {
  build?: typeof buildMeasure;
} = {};

// declare namespace Measure {
//   export function build<Q extends Quantity>(units: Unit<Q>, v1: number): Scalar<Q>;
//   export function build<Q extends Quantity>(
//     units: Unit<Q>,
//     v1: number,
//     v2: number
//   ): Vector<Q, 2>;
//   export function build<Q extends Quantity>(
//     units: Unit<Q>,
//     v1: number,
//     v2?: number
//   ): Scalar<Q> | Vector<Q, 2>;
// }

// A `Unit` of measure. Measures of the same `Quantity` can be directly compared after conversion.
// Every Quantity has a fundamental unit scale which is utilized directly by the Babylon.js engine.
export class Unit<Q extends Quantity> {
  private constructor(
    readonly rawToUnits: number,
    readonly label: string,
    readonly symbol: string,
    readonly quantity: Q,
    // DNS: Apply this, but when??
    readonly isInteger = false,
  ) {}

  static create<Q extends Quantity>(
    rawToUnits: number,
    label: string,
    symbol: string,
    quantity: Q,
    isInteger = false,
  ) {
    const units = new Unit<Q>(rawToUnits, label, symbol, quantity, isInteger);
    function build(...v: [number]): Scalar<Q>;
    function build(...v: [number, number]): Vector<Q, 2>;
    function build(...v: [number, number, number]): Vector<Q, 3>;
    function build(...v: [number, number, number, number]): Vector<Q, 4>;
    function build(...v: number[]): Scalar<Q> | Vector<Q, 2> | Vector<Q, 3> | Vector<Q, 4> {
      return Factory.build!(
        // This cast will be "true" after the Object.assign below.
        build as unknown as Unit<Q>,
        ...(v.map((vi) => vi / rawToUnits) as [number, number]),
      );
    }

    // const buildMeasure = (v1: number, v2?: number) => build(units, v1, v2);
    // https://stackoverflow.com/a/41853194/8107589
    // https://stackoverflow.com/a/55676767/8107589
    // For this pattern/hack to work, we need the function to be first. But, we prefer the type
    // of the unit to come first for clarity, so we swap the order in the type cast.
    const result = Object.assign(build, units);
    Object.setPrototypeOf(result, Unit.prototype);
    Object.defineProperty(result, 'name', { value: capitalize(labelToProp(label)) });
    return result;
  }

  static of<Q extends Quantity>(
    multiple: number,
    unit: Unit<Q>,
    label: string,
    symbol: string,
    isInteger = false,
  ) {
    return Unit.create(unit.rawToUnits / multiple, label, symbol, unit.quantity, isInteger);
  }

  equals(rhs: Unit<Quantity>) {
    return (
      this === rhs ||
      (this.quantity.label === rhs.quantity.label &&
        this.rawToUnits === rhs.rawToUnits &&
        this.isInteger === rhs.isInteger)
    );
  }

  dot<Q2 extends Quantity>(unit: Unit<Q2>) {
    const rawToUnits = this.rawToUnits * unit.rawToUnits;
    let unitLabel = `${this.label} dot ${unit.label}`;
    while (unitLabel in unitLabelAliases) {
      unitLabel = unitLabelAliases[unitLabel];
    }
    let unitSymbol =
      this.symbol && unit.symbol
        ? `${this.symbol}${Punct.Dot}${unit.symbol}`
        : !this.symbol
          ? unit.symbol
          : this.symbol;
    while (unitSymbol in unitAliases) {
      unitSymbol = unitAliases[unitSymbol];
    }
    let quantityLabel = `${this.quantity.label} * ${unit.quantity.label}`;
    while (quantityLabel in quantityTypeAliases) {
      quantityLabel = quantityTypeAliases[quantityLabel];
    }
    return Unit.create(
      rawToUnits,
      unitLabel,
      unitSymbol,
      new ProductOf<Q, Q2>(quantityLabel), // , this.quantity, unit.quantity)
    );
  }

  per<Q2 extends Quantity>(denominator: Unit<Q2>) {
    const rawToUnits = this.rawToUnits / denominator.rawToUnits;
    let unitLabel = `${this.label} per ${denominator.label}`;
    while (unitLabel in unitLabelAliases) {
      unitLabel = unitLabelAliases[unitLabel];
    }
    let unitSymbol = `${this.symbol}${Punct.Dot}${denominator.symbol}${Punct.NegOne}`;
    while (unitSymbol in unitAliases) {
      unitSymbol = unitAliases[unitSymbol];
    }
    let quantityLabel = `${this.quantity.label} / ${denominator.quantity.label}`;
    while (quantityLabel in quantityTypeAliases) {
      quantityLabel = quantityTypeAliases[quantityLabel];
    }
    return Unit.create(
      rawToUnits,
      unitLabel,
      unitSymbol,
      new RatioOf<Q, Q2>(quantityLabel), // , this.quantity, denominator.quantity)
    );
  }
}

export type UnitOf<Q extends Quantity> = ReturnType<typeof Unit.of<Q>>;
